import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// import { useBranch } from '../contexts/BranchContext';
import { useLanguage /*, SUPPORTED_LANGUAGES */ } from '../contexts/LanguageContext';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import { API_URL } from '../utils/apiConfig';
import SubscriptionModal from '../components/SubscriptionModal';
import PlanRenewalModal from '../components/PlanRenewalModal';
import ElectronModal from '../components/ElectronModal';
import '../GreenMistTheme.css';

function HomePage() {
    const navigate = useNavigate();
    const { currentUser, logout, token, isLoading: authLoading } = useAuth();
    // const { selectedBranch, branches, setSelectedBranch } = useBranch();
    const { t /*, language, changeLanguage */ } = useLanguage();

    const getNavItemLabel = (item) => {
        if (item.id === 'customer') {
            return currentUser?.company_id === 38 ? 'Customers' : 'Customer View';
        }
        return t(item.id) || item.label;
    };

    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
    const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);

    // State for frame navigation
    const [activeFrame, setActiveFrame] = useState(null);
    const [frameUrl, setFrameUrl] = useState('');
    const [isFrameLoading, setIsFrameLoading] = useState(false);
    const [companyInfo, setCompanyInfo] = useState(null);
    const planFeatures = usePlanFeatures(companyInfo);
    
    // State for Electron modal
    const [electronModalPath, setElectronModalPath] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const fetchCompanyInfo = async () => {
            // Don't fetch if auth is clearly loading (avoids race condition on refresh)
            if (authLoading) return;

            try {
                // If user is logged in (has token), use authenticated endpoint
                const url = token
                    ? `${API_URL}/api/company/profile`
                    : `${API_URL}/api/company/public`;

                const options = token
                    ? { headers: { 'Authorization': `Bearer ${token}` } }
                    : {};

                // console.log('[HomePage] Fetching company info from:', url);
                const res = await fetch(url, options);

                if (res.ok) {
                    const json = await res.json();
                    if (isMounted && json.success && json.data) {
                        console.log('[HomePage] Company Info Loaded:', json.data);
                        setCompanyInfo(json.data);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
            }
        };

        fetchCompanyInfo();

        return () => {
            isMounted = false;
        };
    }, [token, authLoading]);

    // --- Navigation Handlers ---
    const handleTopNavigation = (path) => {
        // Check if running in Electron
        const isElectron = window.navigator.userAgent.toLowerCase().includes('electron');
        
        if (isElectron) {
            // In Electron, open modal with actual React component
            setElectronModalPath(path);
        } else {
            // In web browser, use iframe modal for preview
            setActiveFrame(path);
            setFrameUrl(path);
            setIsFrameLoading(true);
        }
    };

    const handleCardNavigation = (path) => {
        // Card navigation goes to full page (for both web and Electron)
        navigate(path);
    };

    const closeFrame = () => {
        setActiveFrame(null);
        setFrameUrl('');
        setIsFrameLoading(false);
    };
    
    const closeElectronModal = () => {
        setElectronModalPath(null);
    };

    const handleIframeLoad = () => {
        setIsFrameLoading(false);
    };

    // --- Effects for UI ---
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // No extra effects needed here for now
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const getFeatureUpgradeNote = (featureId) => {
        if (planFeatures.isTrial) return '';
        if (featureId === 'analytics' && !planFeatures.features.advancedAnalytics) {
            return 'Basic analytics is available. Upgrade for full analytics reports and downloads.';
        }
        if (featureId === 'ingredients' && !planFeatures.features.ingredients) {
            return 'Ingredients & inventory management is available on Premium only.';
        }
        if (featureId === 'staff' && !planFeatures.features.leaveManagement) {
            return 'Attendance is available on Starter; upgrade for leaves and Excel export.';
        }
        return '';
    };

    const allFeatures = [
        {
            id: 'admin',
            title: t('adminDashboardFeature'),
            description: t('adminDesc'),
            path: '/admin.html',
            buttonColor: 'bg-indigo-600 hover:bg-indigo-700',
            demoText: t('viewAdminPanel'),
            isLink: false
        },
        {
            id: 'kitchen',
            title: t('kitchenDisplayFeature'),
            description: t('kitchenDesc'),
            path: '/kitchen.html',
            buttonColor: 'bg-blue-600 hover:bg-blue-700',
            demoText: t('viewKitchenDisplay'),
            isLink: false
        },
        {
            id: 'customer',
            title: t('customerExperience'),
            description: t('customerDesc'),
            path: '/customer.html',
            buttonColor: 'bg-purple-600 hover:bg-purple-700',
            demoText: t('viewCustomerApp'),
            isLink: false
        },
        {
            id: 'analytics',
            title: t('analyticsReports'),
            description: t('analyticsDesc'),
            path: '/analytics.html',
            buttonColor: 'bg-green-600 hover:bg-green-700',
            demoText: t('viewAnalytics'),
            isLink: false,
            note: getFeatureUpgradeNote('analytics')
        },
        {
            id: 'ingredients',
            title: t('ingredientsManagement'),
            description: t('ingredientsDesc'),
            path: '/ingredients',
            buttonColor: 'bg-orange-600 hover:bg-orange-700',
            demoText: t('manageIngredients'),
            isLink: false,
            note: getFeatureUpgradeNote('ingredients')
        },
        {
            id: 'staff',
            title: t('attendanceManagement'),
            description: t('staffDesc'),
            path: '/staff',
            buttonColor: 'bg-teal-600 hover:bg-teal-700',
            demoText: t('staffLogin'),
            isLink: false,
            note: getFeatureUpgradeNote('staff')
        }
    ];

    const filterItems = (item) => {
        if (!currentUser) return false;

        const roleStr = (currentUser.role || '').toLowerCase();
        // System-level roles: admin, owner, superadmin — see EVERYTHING
        if (roleStr === 'admin' || roleStr === 'owner' || roleStr === 'superadmin') return true;

        // Company creator / super admin: no branch restriction AND no role-based permission limits
        // If a user has no permissions object at all (null/undefined) and is not a customer, they're a super admin
        if (!currentUser.permissions && roleStr !== 'customer' && roleStr !== 'management') return true;

        // Parse permissions safely
        let perms = currentUser.permissions;
        if (typeof perms === 'string') {
            try { perms = JSON.parse(perms); } catch (e) { perms = {}; }
        }

        if (perms) {
            if (item.id === 'admin') {
                // Admin tab/feature is visible if they have admin permission or any sub-admin tab permissions
                return !!(perms.admin || perms.menu || perms.tables || perms.staff || perms.users || perms.roles || perms.branches || perms.settings);
            }
            
            // Check specific permission mapping for each feature
            return !!perms[item.id];
        }

        // Regular Customers
        if (roleStr === 'customer') {
            return item.id === 'customer';
        }

        return false;
    };

    const visibleFeatures = allFeatures.filter(filterItems);
    // console.log('[HomePage] Visible Features Count:', visibleFeatures.length);

    const navItems = [
        { id: 'admin', path: '/admin.html', icon: 'fa-cog', label: t('admin') },
        { id: 'kitchen', path: '/kitchen.html', icon: 'fa-chef-hat', label: t('kitchen') },
        { id: 'customer', path: '/customer.html', icon: 'fa-user', label: t('customers') },
        { id: 'analytics', path: '/analytics.html', icon: 'fa-chart-line', label: t('analytics') },
        { id: 'ingredients', path: '/ingredients', icon: 'fa-cubes', label: t('ingredients') },
        { id: 'staff', path: '/staff', icon: 'fa-users', label: t('attendanceManagement') }
    ];

    const visibleNavItems = navItems.filter(filterItems);


    // ── Trial Logic ──────────────────────────────
    const trialStatus = (() => {
        console.log('[HomePage] Checking Trial Status. Info:', companyInfo);

        if (!companyInfo) {
            console.log('[HomePage] No company info yet.');
            return null;
        }
        if (companyInfo.has_paid) {
            console.log('[HomePage] Company has paid.');
            return null;
        }
        if (!companyInfo.trial_ends_at) {
            console.log('[HomePage] No trial_ends_at date.');
            return null;
        }

        const end = new Date(companyInfo.trial_ends_at);
        const now = new Date();
        // Check strict expiration (time-based) for blocking or red status
        const isExpired = end < now;

        // Calculate calendar days for display (Date-based)
        const endDay = new Date(end);
        endDay.setHours(0, 0, 0, 0);
        const nowDay = new Date(now);
        nowDay.setHours(0, 0, 0, 0);

        const diffTime = endDay - nowDay;
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

        console.log(`[HomePage] Trial expires: ${end}, Days left: ${diffDays}`);

        if (isExpired) return { expired: true, days: 0 };
        return { expired: false, days: diffDays };
    })();

    return (
        <div className="green-mist-theme min-h-screen">

            <div className="min-h-screen pt-0 relative z-0">

                {/* ── FULL PAGE BLOCKER FOR EXPIRED TRIAL (ADMIN ONLY) ── */}
                {trialStatus?.expired && !companyInfo?.has_paid && currentUser?.role === 'admin' && (
                    <div className="fixed inset-0 z-[99999] bg-gray-50 flex flex-col items-center justify-center p-4">
                        <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-2xl w-full text-center border border-gray-100">
                            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                                <i className="fas fa-lock text-5xl text-red-600"></i>
                            </div>
                            <h1 className="text-4xl font-black text-gray-900 mb-4">{t('trialExpiredTitle')}</h1>
                            <p
                                className="text-xl text-gray-600 mb-8 max-w-lg mx-auto"
                                dangerouslySetInnerHTML={{ __html: t('trialExpiredDesc').replace('{name}', companyInfo?.name || 'DineFlow') }}
                            />

                            <div className="grid md:grid-cols-3 gap-6 mb-8 text-left max-w-lg mx-auto">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-check text-green-600"></i>
                                    </div>
                                    <span className="font-medium text-gray-700">{t('unlimitedOrders')}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-check text-green-600"></i>
                                    </div>
                                    <span className="font-medium text-gray-700">{t('detailedAnalytics')}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-check text-green-600"></i>
                                    </div>
                                    <span className="font-medium text-gray-700">{t('support247')}</span>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/pricing')}
                                className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white text-xl font-bold py-4 px-10 rounded-xl shadow-lg transform hover:-translate-y-1 transition duration-200 animate-bounce"
                            >
                                <i className="fas fa-rocket mr-2"></i> {t('upgradeNowToContinue')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Fixed Header & Banner Container - Hidden when preview is active to avoid overlap */}
                <div className={`fixed top-0 left-0 w-full z-[9999] header-wrapper ${isScrolled ? 'scrolled' : ''} ${activeFrame ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                    {/* Trial Banner or Flash Sale Banner - Part of fixed block */}
                    {trialStatus ? (
                        <div className={`${trialStatus.expired ? 'bg-red-600' : 'trial-banner-gradient'} text-white py-2.5 px-4 relative z-[2100]`}>
                            <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
                                <div className="flex items-center gap-2 text-sm md:text-base">
                                    <i className={`fas ${trialStatus.expired ? 'fa-exclamation-circle' : 'fa-gift'} text-xl`}></i>
                                    <span className="font-black text-center sm:text-left text-base md:text-lg tracking-wide">
                                        {trialStatus.expired
                                            ? t('trialExpiredBanner')
                                            : trialStatus.days === 0
                                                ? t('trialExpiresToday')
                                                : t('trialDaysLeft').replace('{days}', trialStatus.days)}
                                    </span>
                                </div>
                                <button
                                    onClick={() => navigate('/pricing')}
                                    className={`text-xs md:text-sm px-6 py-2 rounded-xl bg-white ${trialStatus.expired ? 'text-red-600' : 'text-emerald-700'} font-black hover:bg-slate-100 transition-all transform hover:scale-105 active:scale-95 whitespace-nowrap shadow-xl border-2 border-transparent hover:border-white/50`}
                                >
                                    {trialStatus.expired ? t('subscribeNow') : t('upgradePlan')}
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {/* Premium Header with Navigation */}
                    <header className="transition-all duration-500 fixed-header w-full relative z-[2000] py-4">

                        <div className="w-full px-4 relative z-10" style={{ overflow: 'visible' }}>
                            {/* Desktop Layout - Flexbox container for better responsiveness */}
                            <div className="header-desktop-nav-container justify-between items-center gap-4 py-2" style={{ overflow: 'visible' }}>
                                {/* Restaurant Name - Left */}
                                <div className="flex items-center space-x-3 group flex-shrink-0 max-w-[25%] lg:max-w-[35%] overflow-hidden">
                                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-white rounded-2xl flex items-center justify-center shadow-md border-2 border-emerald-500 transform transition-all duration-300 hover:rotate-3 group-hover:scale-110 p-1.5">
                                        {companyInfo?.logo_url ? (
                                            <img src={companyInfo.logo_url} alt="Logo" className="w-full h-full object-contain rounded-xl" />
                                        ) : (
                                            <img src="/app-logo.png" alt="RedSorm" className="w-full h-full object-contain rounded-xl" />
                                        )}
                                    </div>
                                    <h1 className="text-2xl lg:text-3xl font-black tracking-tight text-slate-900 group-hover:text-emerald-600 transition-colors">
                                        {companyInfo ? companyInfo.name : 'Restaurant'}
                                    </h1>
                                </div>

                                {/* Desktop Navigation - Center */}
                                <nav className="flex-1 flex flex-wrap items-center justify-center gap-1.5 lg:gap-2">
                                    {visibleNavItems.map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => item.id === 'home' ? handleCardNavigation(item.path) : handleTopNavigation(item.path)}
                                            className="nav-item px-5 py-2.5 rounded-xl font-extrabold flex items-center space-x-2 transition-all duration-200 text-slate-700 hover:text-indigo-600 whitespace-nowrap"
                                        >
                                            <i className={`fas ${item.icon}`}></i>
                                            <span>{getNavItemLabel(item)}</span>
                                        </button>
                                    ))}
                                </nav>

                                {/* User Profile Section - Right */}
                                <div className="flex items-center justify-end flex-shrink-0 w-48 lg:w-64 relative">
                                    {currentUser && (
                                        <div className="relative z-[10002]">
                                            <button
                                                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                                                className="flex items-center space-x-3 px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-all duration-300"
                                            >
                                                <div className="text-right hidden lg:block">
                                                    <p className="text-sm font-bold leading-none text-slate-900">{currentUser.full_name || 'User'}</p>
                                                    <p className="text-xs text-slate-500 leading-none mt-1 max-w-[100px] truncate">{currentUser.email}</p>
                                                </div>
                                                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-white rounded-full flex items-center justify-center text-purple-600 shadow-md">
                                                    <i className="fas fa-user text-sm lg:text-base"></i>
                                                </div>
                                                <i className={`fas fa-chevron-down text-xs text-slate-400 transition-transform duration-300 ${isProfileDropdownOpen ? 'rotate-180' : ''}`}></i>
                                            </button>

                                            {/* Desktop Profile Dropdown */}
                                            {/* Desktop Profile Dropdown */}
                                            {isProfileDropdownOpen && (
                                                <>
                                                    {/* Backdrop to close dropdown */}
                                                    <div
                                                        className="fixed inset-0 z-[9999]"
                                                        onClick={() => setIsProfileDropdownOpen(false)}
                                                    ></div>

                                                    <div className="profile-dropdown absolute top-full right-0 mt-4 w-64 bg-white rounded-2xl shadow-2xl overflow-hidden z-[10000] animate-fade-in border border-gray-100">
                                                        <div className="profile-dropdown-header p-5 bg-emerald-50 border-b border-emerald-100 text-left">
                                                            <p className="font-black text-lg text-slate-900">{currentUser.full_name || 'User'}</p>
                                                            <p className="text-xs text-emerald-700 mt-1 font-medium">{currentUser.email}</p>
                                                            <div className="mt-3 inline-block px-2 py-1 bg-white border border-emerald-200 rounded-md text-[10px] uppercase font-bold tracking-widest text-emerald-800 shadow-sm">
                                                                {currentUser.role}
                                                            </div>
                                                        </div>
                                                        <div className="p-2 bg-white space-y-1">
                                                            {((currentUser?.role || '').toLowerCase() === 'admin' ||
                                                              (currentUser?.role || '').toLowerCase() === 'owner' ||
                                                              (currentUser?.role || '').toLowerCase() === 'superadmin') && (
                                                                <button
                                                                    onClick={() => {
                                                                        setIsRenewalModalOpen(true);
                                                                        setIsProfileDropdownOpen(false);
                                                                    }}
                                                                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-left rounded-xl hover:bg-indigo-50 transition-all duration-200 text-indigo-600 font-extrabold text-xs"
                                                                >
                                                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600">
                                                                        <i className="fas fa-sync-alt"></i>
                                                                    </div>
                                                                    <span>Renew / Extend Plan</span>
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => {
                                                                    logout();
                                                                    setIsProfileDropdownOpen(false);
                                                                }}
                                                                className="logout-btn w-full flex items-center space-x-3 px-4 py-3 text-left rounded-xl transition-all duration-200 group"
                                                            >
                                                                <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-all">
                                                                    <i className="fas fa-sign-out-alt"></i>
                                                                </div>
                                                                <span className="font-bold">{t('logout')}</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Mobile Layout */}
                            <div className="header-mobile-nav-container py-2">
                                {/* Restaurant Name - Mobile */}
                                <div className="flex items-center space-x-3 group">
                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center transform transition-all duration-300 group-hover:rotate-12 group-hover:scale-110 shadow-lg p-1.5">
                                        {companyInfo?.logo_url ? (
                                            <img src={companyInfo.logo_url} alt="Logo" className="w-full h-full object-contain rounded-full" />
                                        ) : (
                                            <img src="/app-logo.png" alt="RedSorm" className="w-full h-full object-contain rounded-full" />
                                        )}
                                    </div>
                                    <h1 className="text-xl font-bold tracking-tight truncate max-w-[60vw] sm:max-w-[250px]">{companyInfo ? companyInfo.name : 'Restaurant'}</h1>
                                </div>

                                {/* Mobile Menu Button */}
                                <button
                                    className="md:hidden p-2 hover:scale-110 transition-transform active:scale-95 bg-white/10 rounded-lg backdrop-blur-md"
                                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                    aria-label="Toggle Menu"
                                >
                                    <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
                                </button>
                            </div>

                            {/* Mobile Menu */}
                            {isMobileMenuOpen && (
                                <nav className="md:hidden mt-4 pb-4 animate-fade-in-down bg-black/20 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                                    <div className="flex flex-wrap gap-2 mb-4 justify-center">
                                        {visibleNavItems.map((item) => (
                                            <button
                                                key={item.id}
                                                onClick={() => {
                                                    item.id === 'home' ? handleCardNavigation(item.path) : handleTopNavigation(item.path);
                                                    setIsMobileMenuOpen(false);
                                                }}
                                                className="mobile-nav-item nav-item bg-white bg-opacity-90 px-4 py-2 rounded-xl font-bold shadow-md flex items-center space-x-2 text-purple-900 w-full sm:w-auto justify-center"
                                            >
                                                <i className={`fas ${item.icon}`}></i>
                                                <span>{getNavItemLabel(item)}</span>
                                            </button>
                                        ))}
                                    </div>
                                    {/* Mobile User Profile & Logout */}
                                    {currentUser && (
                                        <div className="bg-white/10 rounded-xl overflow-hidden border border-white/10">
                                            <button
                                                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                                                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-all duration-300"
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-purple-600 shadow-md">
                                                        <i className="fas fa-user"></i>
                                                    </div>
                                                    <div className="flex-1 text-left text-white">
                                                        <p className="text-sm font-bold leading-none">{currentUser.full_name || 'User'}</p>
                                                        <p className="text-xs opacity-75 leading-none mt-1">{currentUser.email}</p>
                                                    </div>
                                                </div>
                                                <i className={`fas fa-chevron-down text-sm text-white transition-transform duration-300 ${isProfileDropdownOpen ? 'rotate-180' : ''}`}></i>
                                            </button>

                                            {/* Collapsible Logout Section */}
                                            {isProfileDropdownOpen && (
                                                <div className="px-4 pb-4 pt-2 border-t border-white/10 animate-fade-in space-y-2">
                                                    {((currentUser?.role || '').toLowerCase() === 'admin' ||
                                                      (currentUser?.role || '').toLowerCase() === 'owner' ||
                                                      (currentUser?.role || '').toLowerCase() === 'superadmin') && (
                                                        <button
                                                            onClick={() => {
                                                                setIsRenewalModalOpen(true);
                                                                setIsProfileDropdownOpen(false);
                                                            }}
                                                            className="w-full px-4 py-3 rounded-xl font-bold bg-indigo-600 text-white shadow-md transition-all duration-300 flex items-center justify-center space-x-2 text-xs"
                                                        >
                                                            <i className="fas fa-sync-alt"></i>
                                                            <span>Renew / Extend Plan</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            logout();
                                                            // Don't update state after logout - page is redirecting
                                                        }}
                                                        className="logout-btn w-full px-4 py-3 rounded-xl font-medium shadow-md transition-all duration-300 flex items-center justify-center space-x-2"
                                                    >
                                                        <i className="fas fa-sign-out-alt"></i>
                                                        <span>{t('logout')}</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </nav>
                            )}
                        </div>
                    </header>

                </div>

                {/* Main Content Area - adjusted top padding dynamically to account for fixed trial banner */}
                <main className={`w-full max-w-[1600px] mx-auto px-6 pb-12 transition-all duration-500 ${trialStatus ? 'pt-36 sm:pt-40 lg:pt-32' : 'pt-20 sm:pt-24 lg:pt-20'}`}>
                    {/* Moved Profile Dropdown logic removed from here as it is at root level */}

                    {/* Hero Section */}
                    <div className="relative rounded-[2rem] overflow-hidden mb-4 shadow-2xl min-h-[200px] md:min-h-[250px] hero-section mt-0">
                        {companyInfo?.banner_url && (
                            <div
                                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105"
                                style={{ backgroundImage: `url(${companyInfo.banner_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-900/60 via-indigo-900/60 to-pink-900/60"></div>
                            </div>
                        )}

                        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center p-4 md:p-6">
                            {companyInfo?.logo_url && (
                                <div className="w-16 h-16 md:w-20 md:h-20 p-1 bg-white rounded-[1.5rem] shadow-2xl mb-3 border-4 border-emerald-500/20">
                                    <img src={companyInfo.logo_url} alt="Logo" className="w-full h-full object-contain rounded-[1.5rem]" />
                                </div>
                            )}
                            <h2 className="text-2xl md:text-4xl font-black mb-3 leading-none tracking-tighter text-slate-900">
                                {t('welcomeTo')} <br />
                                <span>
                                    {companyInfo ? companyInfo.name : t('restaurant')}
                                </span>
                            </h2>
                            <p className="text-sm md:text-base max-w-3xl mx-auto leading-relaxed mb-2 text-slate-500 font-medium">
                                {t('heroDescription')}
                            </p>
                        </div>
                    </div>

                    {/* Feature Cards Grid - Fixed Grid Layout */}
                    <div className="features-grid mb-4">
                        {visibleFeatures.map((feature, index) => {
                            // Define images/icons for each feature
                            const featureImages = {
                                'customer': { icon: 'fa-mobile-screen-button', gradient: 'from-purple-500 to-pink-500' },
                                'kitchen': { icon: 'fa-fire-burner', gradient: 'from-blue-500 to-cyan-500' },
                                'admin': { icon: 'fa-gauge-high', gradient: 'from-indigo-500 to-purple-500' },
                                'analytics': { icon: 'fa-chart-line', gradient: 'from-green-500 to-emerald-500' },
                                'ingredients': { icon: 'fa-boxes-stacked', gradient: 'from-orange-500 to-red-500' },
                                'staff': { icon: 'fa-user-clock', gradient: 'from-teal-500 to-cyan-500' }
                            };

                            const featureImage = featureImages[feature.id] || { icon: 'fa-star', gradient: 'from-gray-500 to-gray-600' };

                            return (
                                <div
                                    key={index}
                                    className="feature-card flex flex-col group active:scale-95 transition-all duration-300 transform-gpu"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    {/* Icon Circle at Top - Moved Higher */}
                                    <div className="flex justify-center pt-4 pb-2">
                                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${featureImage.gradient} flex items-center justify-center shadow-xl transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                                            <i className={`fas ${featureImage.icon} text-2xl text-white`}></i>
                                        </div>
                                    </div>

                                    <div className="px-3 pb-3 pt-2 flex flex-col flex-grow">
                                        <h3 className="text-sm font-extrabold mb-2 text-slate-900 group-hover:text-emerald-600 transition-colors uppercase tracking-tight leading-tight text-center">{feature.title}</h3>
                                        <p className="font-medium mb-2 leading-snug flex-grow text-slate-600 text-[11px] text-center">{feature.description}</p>
                                        {feature.note && (
                                            <p className="text-[11px] text-slate-300 italic mb-3">{feature.note}</p>
                                        )}
                                        <div className="mt-auto">
                                            <button
                                                onClick={() => handleCardNavigation(feature.path)}
                                                className={`${feature.buttonColor || 'bg-emerald-600 hover:bg-emerald-700'} block w-full text-center py-2 px-3 rounded-lg transition-all duration-300 font-black text-white uppercase tracking-wide shadow-lg hover:shadow-xl hover:-translate-y-1 active:scale-110 active:rotate-1 text-[10px]`}
                                            >
                                                {feature.demoText}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Why Choose Us Section */}
                    < div className="glass-effect rounded-3xl shadow-2xl p-10 mb-16 why-choose-us-section" >
                        <h3 className="text-3xl font-bold text-gray-800 mb-10 text-center">{t('whyChooseTitle')}</h3>
                        <div className="features-side-by-side flex flex-col md:flex-row justify-center gap-12 px-4">
                            {[
                                {
                                    icon: 'fa-mobile-alt',
                                    title: t('mobileFirstTitle'),
                                    description: t('mobileFirstDesc')
                                },
                                {
                                    icon: 'fa-bolt',
                                    title: t('lightningFastTitle'),
                                    description: t('lightningFastDesc')
                                },
                                {
                                    icon: 'fa-shield-alt',
                                    title: t('secureReliableTitle'),
                                    description: t('secureReliableDesc')
                                }
                            ].map((item, index) => (
                                <div key={index} className="text-center group p-6 rounded-2xl transition-all duration-300">
                                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-2xl">
                                        <i className={`fas ${item.icon} text-3xl`}></i>
                                    </div>
                                    <h4 className="font-extrabold text-xl text-slate-900 mb-3">{item.title}</h4>
                                    <p className="text-slate-600 font-medium leading-relaxed">{item.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Testimonial Section */}
                    <div className="testimonial-role-gradient rounded-3xl shadow-xl p-12 text-slate-900 mb-16 relative overflow-hidden border-2 border-emerald-100">
                        <div className="absolute top-0 left-0 w-full h-full bg-black opacity-10"></div>
                        <div className="relative z-10 max-w-4xl mx-auto text-center">
                            <i className="fas fa-quote-left text-6xl mb-8 opacity-50"></i>
                            <p className="text-2xl md:text-3xl mb-8 italic leading-relaxed">
                                "{t('testimonialQuote')}"
                            </p>
                            <div className="flex items-center justify-center">
                                <img src="https://picsum.photos/seed/restaurant-owner/60/60.jpg" alt="Restaurant Owner" className="w-16 h-16 rounded-full mr-6 border-4 border-white shadow-lg" />
                                <div className="text-left">
                                    <p className="font-bold text-xl">{t('testimonialAuthor')}</p>
                                    <p className="text-lg opacity-90">{t('testimonialRole')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>



                {/* Footer */}
                <footer className="bg-gray-900 text-white py-12 px-4 mt-16">
                    <div className="max-w-[1600px] mx-auto">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
                            <div className="col-span-2 md:col-span-1">
                                <div className="flex items-center space-x-3 mb-6">
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '50%',
                                        background: '#fff',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        padding: '4px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                    }}>
                                        <img src="/app-logo.png" alt="RedSorm" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    </div>
                                    <h3 className="text-xl font-bold logo-text">RedSorm</h3>
                                </div>
                                <p className="text-gray-400 leading-relaxed">{t('footerTagline')}</p>
                            </div>
                            <div className="col-span-1">
                                <h4 className="font-bold text-lg mb-4">{t('product')}</h4>
                                <ul className="space-y-3 text-gray-400">
                                    <li><button onClick={() => navigate('/features')} className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block cursor-pointer">{t('features')}</button></li>
                                    <li><button onClick={() => navigate('/dashboard')} className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block cursor-pointer">{t('pricing')}</button></li>
                                    <li><button onClick={() => navigate('/faqs')} className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block cursor-pointer">{t('faqs')}</button></li>
                                </ul>
                            </div>
                            <div className="col-span-1">
                                <h4 className="font-bold text-lg mb-4">{t('company')}</h4>
                                <ul className="space-y-3 text-gray-400">
                                    <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block cursor-pointer">{t('about')}</button></li>
                                    <li><button className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block" onClick={(e) => e.preventDefault()}>{t('blog')}</button></li>
                                    <li><button className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block" onClick={(e) => e.preventDefault()}>{t('careers')}</button></li>
                                </ul>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <h4 className="font-bold text-lg mb-4">{t('connect')}</h4>
                                <ul className="space-y-3 text-gray-400">
                                    <li><a href="https://x.com/Redsorm_S" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block"><i className="fab fa-twitter mr-2"></i>Twitter</a></li>
                                    <li><a href="https://www.facebook.com/profile.php?id=61590644184687" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block"><i className="fab fa-facebook mr-2"></i>Facebook</a></li>
                                    <li><a href="https://www.instagram.com/redsorm.in/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block"><i className="fab fa-instagram mr-2"></i>Instagram</a></li>
                                    <li><a href="https://www.youtube.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block"><i className="fab fa-youtube mr-2"></i>YouTube</a></li>
                                    <li><a href="https://www.linkedin.com/company/117164495/admin/dashboard/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block"><i className="fab fa-linkedin mr-2"></i>LinkedIn</a></li>
                                </ul>
                            </div>
                        </div>
                        <div className="border-t border-gray-800 pt-8 text-center text-gray-500">
                            <p>&copy; 2024 RedSorm. {t('copyright')}</p>
                        </div>
                    </div>
                </footer>





                {/* Iframe Modal for Top Navigation - Using low-intensity subtle blur */}
                {activeFrame && (
                    <div className="fixed inset-0 bg-black/10 backdrop-blur-md z-[100000] flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
                                <h3 className="text-lg font-bold">{t('preview')}</h3>
                                <button
                                    onClick={closeFrame}
                                    className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-full"
                                >
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>

                            {/* Iframe Content */}
                            <div className="flex-1 relative">
                                {isFrameLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                        <div className="text-center">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                                            <p className="text-gray-600">{t('loading')}</p>
                                        </div>
                                    </div>
                                )}
                                <iframe
                                    src={frameUrl}
                                    className="w-full h-full border-0"
                                    title="Preview"
                                    onLoad={handleIframeLoad}
                                />
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Electron Modal for Top Navigation - Renders actual React components */}
                {electronModalPath && (
                    <ElectronModal 
                        path={electronModalPath} 
                        onClose={closeElectronModal} 
                    />
                )}
                
                {/* Subscription Modal */}
                <SubscriptionModal
                    isOpen={isSubscriptionOpen}
                    onClose={() => setIsSubscriptionOpen(false)}
                />

                {/* Plan Renewal Modal */}
                <PlanRenewalModal
                    isOpen={isRenewalModalOpen}
                    onClose={() => setIsRenewalModalOpen(false)}
                    companyProfile={companyInfo}
                    token={token}
                    onSuccess={() => {
                        if (token) {
                            fetch(`${API_URL}/api/company/profile`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            })
                            .then(res => res.json())
                            .then(json => {
                                if (json.success && json.data) {
                                    setCompanyInfo(json.data);
                                }
                            })
                            .catch(() => {});
                        }
                    }}
                />
            </div>
        </div>
    );
}

export default HomePage;
